// FirewallCertificates.tsx
import { useState, useEffect, useCallback, useRef } from "react";
import {
    RefreshCw, Loader2, Search, ShieldCheck,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, Clock, Info
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../../components/ItemsPerPageDropdown ";
import { createPortal } from "react-dom";

// ── Types ─────────────────────────────────────────────────────────────────────
interface CertificateItem {
    id: number;
    created: string;
    updated: string;
    asset_id: number;
    firewall_type: string;
    name: string;
    status: string | null;
    state: string;
    source: string;
    range: string;
    comments: string;
    enroll_protocol: string;
    last_updated: number;
    auto_regenerate_days: number;
    auto_regenerate_days_warn: number;
    ike_localid: string;
    ike_localid_type: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtUnix = (unix: number): string => {
    if (!unix) return "—";
    return new Date(unix * 1000).toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
    });
};

// ── Sort Icon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ column, sortConfig }: {
    column: string;
    sortConfig: { key: string; direction: "asc" | "desc" } | null;
}) => {
    if (sortConfig?.key !== column) return (
        <div className="opacity-0 group-hover:opacity-40 transition-opacity">
            <ArrowUp size={10} className="text-slate-400 dark:text-slate-500" />
        </div>
    );
    return sortConfig.direction === "asc"
        ? <ArrowUp size={10} className="text-green-600 dark:text-green-400" />
        : <ArrowDown size={10} className="text-green-600 dark:text-green-400" />;
};

// ── Truncated Comment with Tooltip ────────────────────────────────────────────
const TruncatedComment = ({ text }: { text: string }) => {
    const [show, setShow] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const ref = useRef<HTMLSpanElement>(null);

    if (!text) return <span className="text-slate-400 dark:text-gray-500 text-[10px]">—</span>;

    const isLong = text.length > 40;
    const preview = isLong ? text.slice(0, 40) + "…" : text;

    const handleEnter = () => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setPos({
                top: rect.top - 10, // tooltip above
                left: rect.right,
            });
        }
        setShow(true);
    };

    const handleLeave = () => setShow(false);

    return (
        <>
            <div className="inline-flex items-center gap-1 max-w-[220px]">
                <span
                    ref={ref}
                    className="text-[10px] text-slate-700 dark:text-gray-200 truncate cursor-default"
                    onMouseEnter={handleEnter}
                    onMouseLeave={handleLeave}
                >
                    {preview}
                </span>

                {isLong && (
                    <Info
                        size={11}
                        className="text-slate-400 hover:text-slate-600 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                        onMouseEnter={handleEnter}
                        onMouseLeave={handleLeave}
                    />
                )}
            </div>

            {show &&
                createPortal(
                    <div
                        style={{
                            position: "fixed",
                            top: pos.top,
                            left: pos.left,
                            transform: "translate(-100%, -100%)",
                        }}
                        className="z-[9999] w-72 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg shadow-2xl p-3 pointer-events-none"
                    >
                        <p className="text-[10px] text-slate-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap break-words">
                            {text}
                        </p>

                        <div className="absolute -bottom-1.5 right-2 w-3 h-3 bg-white dark:bg-gray-800 border-r border-b border-slate-200 dark:border-gray-700 rotate-45" />
                    </div>,
                    document.body
                )}
        </>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallCertificates({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [items, setItems] = useState<CertificateItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;
            if (client_id) queryObj["client_id"] = client_id;
            if (debouncedSearch) queryObj.$or = [
                { name: { $ilike: `%${debouncedSearch}%` } },
                { source: { $ilike: `%${debouncedSearch}%` } },
                { enroll_protocol: { $ilike: `%${debouncedSearch}%` } },
            ];
            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit,
            };
            if (sortConfig) { params.sort_by = sortConfig.key; params.sort_order = sortConfig.direction; }
            const res = await baseapi.get("/firewall_certificates", { params });
            if (res.data.status) {
                setItems(res.data.data);
                setTotalRecords(res.data.total ?? res.data.data.length);
            } else showError("Failed to load certificates");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id, debouncedSearch, currentPage, limit, sortConfig]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSort = (key: string) => {
        setSortConfig(prev =>
            prev?.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    const Th = ({ label, col, right = false }: { label: string; col: string; right?: boolean }) => (
        <th
            className={`px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap ${right ? "text-right" : ""}`}
            onClick={() => handleSort(col)}
        >
            <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
                {label} <SortIcon column={col} sortConfig={sortConfig} />
            </div>
        </th>
    );

    return (
        <div className="flex flex-col bg-transparent text-slate-800 dark:text-gray-200">

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-green-500/10 dark:bg-green-600/20 p-1.5 rounded-lg border border-green-500/20 dark:border-green-500/30">
                        <ShieldCheck size={16} className="text-green-600 dark:text-green-400" />
                    </div>
                    <h1 className="text-lg font-bold text-slate-900 dark:text-white tracking-wide">Certificates</h1>
                    <span className="text-[10px] font-bold text-slate-500 dark:text-gray-400 bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 px-2 py-0.5 rounded">
                        {totalRecords} entries
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-gray-500 group-focus-within:text-green-600 dark:group-focus-within:text-green-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search name, source..."
                            className="w-full bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-slate-800 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-green-500/50 transition-all shadow-sm dark:shadow-none"
                        />
                    </div>
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-gray-700 transition-all group shrink-0 shadow-sm dark:shadow-none"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-green-600 dark:group-hover:text-green-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-slate-200 dark:border-gray-700/50">
                                <Th label="Name" col="name" />
                                <Th label="Source" col="source" />
                                <Th label="Range" col="range" />
                                <Th label="Enroll Protocol" col="enroll_protocol" />
                                <Th label="IKE Local ID Type" col="ike_localid_type" />
                                <Th label="Auto Regen Days" col="auto_regenerate_days" right />
                                <Th label="Last Updated" col="last_updated" right />
                                <Th label="Comment" col="comments" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-gray-400">
                                            <Loader2 className="w-4 h-4 animate-spin text-green-600 dark:text-green-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading certificates...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <ShieldCheck size={24} className="text-slate-400 dark:text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">No certificates found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                items.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-gray-700/30 transition-colors">

                                        {/* Name */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[11px] font-bold text-slate-800 dark:text-gray-100">{item.name.replace(/_/g, " ")}</span>
                                            </div>
                                        </td>

                                        {/* Source */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase ${item.source === "factory"
                                                ? "bg-slate-100 dark:bg-gray-700/50 border-slate-200 dark:border-gray-600/40 text-slate-600 dark:text-gray-300"
                                                : "bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400"
                                                }`}>
                                                {item.source}
                                            </span>
                                        </td>

                                        {/* Range */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <span className="text-[10px] text-slate-600 dark:text-gray-300">{item.range || "—"}</span>
                                        </td>

                                        {/* Enroll Protocol */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase ${item.enroll_protocol === "none"
                                                ? "bg-slate-100 dark:bg-gray-700/40 border-slate-200 dark:border-gray-600/30 text-slate-500 dark:text-gray-400"
                                                : "bg-purple-500/10 border-purple-500/20 text-purple-600 dark:text-purple-400"
                                                }`}>
                                                {item.enroll_protocol}
                                            </span>
                                        </td>

                                        {/* IKE Local ID Type */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <span className="font-mono text-[10px] text-slate-600 dark:text-gray-300">{item.ike_localid_type}</span>
                                        </td>

                                        {/* Auto Regen Days */}
                                        <td className="px-4 py-2.5 whitespace-nowrap text-center">
                                            <span className={`font-mono text-[10px] font-semibold ${item.auto_regenerate_days > 0 ? "text-green-600 dark:text-green-400" : "text-slate-400 dark:text-gray-400"}`}>
                                                {item.auto_regenerate_days !== null && item.auto_regenerate_days !== undefined
                                                    ? `${item.auto_regenerate_days}d`
                                                    : "—"}
                                            </span>
                                        </td>

                                        {/* Last Updated */}
                                        <td className="px-4 py-2.5 whitespace-nowrap text-center">
                                            <div className="flex items-center justify-center gap-1 text-slate-500 dark:text-gray-400">
                                                <Clock size={10} className="text-slate-400 dark:text-gray-400" />
                                                <span className="text-[10px]">{fmtUnix(item.last_updated)}</span>
                                            </div>
                                        </td>

                                        {/* Comment — truncated with tooltip */}
                                        <td className="px-4 py-2.5">
                                            <TruncatedComment text={item.comments} />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 dark:bg-gray-800/40 border-t border-slate-200 dark:border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-slate-500 dark:text-gray-400 tracking-widest">
                            <span className="text-slate-700 dark:text-gray-300 font-semibold">{startRecord}</span>
                            {" - "}
                            <span className="text-slate-700 dark:text-gray-300 font-semibold">{endRecord}</span>
                            <span className="mx-1 text-slate-400 dark:text-gray-600">/</span>
                            <span className="text-slate-700 dark:text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button onClick={() => setSearchTerm("")}
                                className="text-[9px] text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-bold uppercase tracking-widest transition-colors">
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
